import { Result, err, ok } from '@hereisyourgitanton/core';
import { Observable, map, forkJoin } from 'rxjs';
import { SealedTradingQueryPort } from '@tradgine/sealed-auction-trade';
import { inject } from '@angular/core';
import { AuctionSealedApi, BidAuctionSealedFlow } from '@tradgine/data-access/auction-sealed-flow';
import {
    TradingProcedureApi,
    LotApi,
    TradingProcedureDocumentApi,
    OrganizationApi,
    OfferSellerApi,
    OfferApi,
} from '@tradgine/data-access/tradgine-data';
import {
    SealedTradingSnapshot,
    SealedTradingProcedure,
    SealedAuction,
    SealedBid,
    SealedLot,
    SealedOffer,
    OrganizationInfo,
    SealedTradingProcedureDocument,
    SealedTradingProcedureRegistry,
} from '@tradgine/sealed-auction-trade';
import {
    toSealedAuction,
    toSealedBid,
    toSealedTradingProcedure,
    toSealedLot,
    toSealedTradingProcedureDocument,
    toSealedTradingProcedureRegistry,
    toSealedOfferFromSellerDto,
    toSealedOfferFromResultDto,
} from '../mappers/mappers';

export class SealedTradingQueryAdapter implements SealedTradingQueryPort {
    private readonly organizationApi = inject(OrganizationApi);
    private readonly tradingProcedureApi = inject(TradingProcedureApi);
    private readonly auctionsApi = inject(AuctionSealedApi);
    private readonly lotsApi = inject(LotApi);
    private readonly bidApi = inject(BidAuctionSealedFlow);
    private readonly tradingProcedureDocumentApi = inject(TradingProcedureDocumentApi);
    private readonly offerSellerApi = inject(OfferSellerApi);
    private readonly offerApi = inject(OfferApi);

    getSnapshot(tradingProcedureId: number): Observable<Result<SealedTradingSnapshot, Error>> {
        return forkJoin({
            tp: this.tradingProcedureApi.getById(tradingProcedureId).pipe(map((result) => result.map(toSealedTradingProcedure))),
            organization: this.organizationApi.getCurrentOrganization(),
            lots: this.lotsApi.getAll(tradingProcedureId).pipe(
                map((result) => result.map((lots) => lots.map(toSealedLot)))
            ),
            auctions: this.auctionsApi.getAll(tradingProcedureId).pipe(
                map((result) => result.map((auctions) => auctions.map(toSealedAuction)))
            ),
            myBids: this.bidApi.getCurrentBids(tradingProcedureId).pipe(
                map((result) => result.map((bids) => bids.map((bid) => toSealedBid(bid, bid.auctionId))))
            ),
        }).pipe(
            map((forkJoinResult) => {
                if (Object.values(forkJoinResult).some((result) => !result.isOk())) {
                    return err<SealedTradingSnapshot, Error>(new Error($localize`:@@sealed-auction.error.load-snapshot:Failed to load sealed trading procedure data`));
                } else {
                    const tp = forkJoinResult.tp.unwrap();
                    const org = forkJoinResult.organization.unwrap();
                    const organizationId = org.id;
                    return ok<SealedTradingSnapshot, Error>({
                        tradingProcedure: tp,
                        organizationId,
                        organizationName: org.name,
                        role: organizationId === tp.sellerOrganizationId ? 'seller' : 'buyer',
                        lots: forkJoinResult.lots.unwrap(),
                        auctions: forkJoinResult.auctions.unwrap(),
                        myBids: forkJoinResult.myBids.unwrap(),
                    });
                }
            })
        );
    }

    getTradingProcedure(tradingProcedureId: number): Observable<Result<SealedTradingProcedure, Error>> {
        return this.tradingProcedureApi.getById(tradingProcedureId).pipe(map((result) => result.map(toSealedTradingProcedure)));
    }

    getAuctions(tradingProcedureId: number): Observable<Result<readonly SealedAuction[], Error>> {
        return this.auctionsApi
            .getAll(tradingProcedureId)
            .pipe(map((result) => result.map((auctions) => auctions.map(toSealedAuction))));
    }

    getAuctionById(auctionId: number): Observable<Result<SealedAuction, Error>> {
        return this.auctionsApi.getById(auctionId).pipe(map((result) => result.map(toSealedAuction)));
    }

    getMyBid(auctionId: number): Observable<Result<SealedBid | null, Error>> {
        return this.bidApi.getCurrentBidbyAuctionId(auctionId).pipe(
            map((result) => {
                // Если Result содержит ошибку, проверяем, не 404 ли это (нет ставки - это нормально)
                if (result.isErr()) {
                    const error = result.unwrapErr();
                    // Если это 404, значит ставки нет - это нормально, возвращаем null
                    if (error.message?.includes('404') || error.message?.includes('Not Found')) {
                        return ok<SealedBid | null, Error>(null);
                    }
                    return err<SealedBid | null, Error>(new Error($localize`:@@sealed-auction.error.fetch-my-bid:Failed to fetch my bid`));
                }
                const bid = result.unwrap();
                if (!bid) {
                    return ok<SealedBid | null, Error>(null);
                }
                return ok<SealedBid | null, Error>(toSealedBid(bid, auctionId));
            })
        );
    }

    getOrganizationId(): Observable<Result<number, Error>> {
        return this.organizationApi.getCurrentOrganization().pipe(
            map((result) =>
                result
                    .map((org) => org.id)
                    .mapErr(() => new Error($localize`:@@sealed-auction.error.fetch-organization:Failed to fetch organization`))
            )
        );
    }

    getTradingProcedureDocuments(tradingProcedureId: number): Observable<Result<SealedTradingProcedureDocument[], Error>> {
        return this.tradingProcedureDocumentApi.getAll(tradingProcedureId).pipe(
            map((result) => result.map((docs) => docs.map(toSealedTradingProcedureDocument)))
        );
    }

    getTradingProcedureRegistry(tradingProcedureId: number): Observable<Result<SealedTradingProcedureRegistry, Error>> {
        return this.tradingProcedureDocumentApi.getRegistryInfo(tradingProcedureId).pipe(
            map((result) => result.map(toSealedTradingProcedureRegistry))
        );
    }

    getRegistryFile(tradingProcedureId: number): Observable<Result<Blob, Error>> {
        return this.tradingProcedureDocumentApi.getRegistry(tradingProcedureId).pipe(
            map((result) => result.mapErr((err) => new Error(err.message)))
        );
    }

    getSealedOffersAsSeller(tradingProcedureId: number): Observable<Result<SealedOffer[], Error>> {
        return this.offerSellerApi.getAll(tradingProcedureId).pipe(
            map((result) => result.map((dtos) => dtos.map(toSealedOfferFromSellerDto)))
        );
    }

    getSealedOffersAsBuyer(tradingProcedureId: number): Observable<Result<SealedOffer[], Error>> {
        return this.offerApi.getResults(tradingProcedureId).pipe(
            map((result) => result.map((dtos) => dtos.map(toSealedOfferFromResultDto)))
        );
    }

    getSellerOrganizationInfos(tpId: number): Observable<Result<OrganizationInfo[], Error>> {
        return this.offerSellerApi.getAll(tpId).pipe(
            map((result) => result.map((dtos) => dtos.map((dto) => ({
                organizationId: dto.organizationId,
                organizationName: dto.organizationName,
            }))))
        );
    }
}

